import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { IEvent, CALENDAR_KEYS } from '@/types/admin/events';
import { ADMIN_COLORS } from '@/constants/admin';
import { getEventStatusColor } from '@/utils/admin/events/status';
import { formatEventType, generateDayEventList } from '@/hooks/admin/useEvents';
import { LazyImage } from '@/components/common/admin/mobile/LazyImage';

interface DesktopMonthlyEventSelectorProps {
  monthlyEvents: IEvent[];
  monthName: string;
  onSelectEvent: (event: IEvent) => void;
  // When true, the selector will expand to fill available vertical space (no fixed height)
  fill?: boolean;
}

export const DesktopMonthlyEventSelector: React.FC<DesktopMonthlyEventSelectorProps> = ({
  monthlyEvents,
  monthName,
  onSelectEvent,
  fill = false,
}) => {
  const dayEventPairings = useMemo(() => generateDayEventList(monthlyEvents), [monthlyEvents]);

  const sortedDays = useMemo(
    () => Object.keys(dayEventPairings).sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
    [dayEventPairings]
  );

  return (
    <View style={[styles.container, fill && styles.containerFlex]}>
      <Text style={styles.title}>{monthName} events</Text>
      <ScrollView style={[styles.scroll, fill && styles.scrollFlex]} showsVerticalScrollIndicator={false}>
        {sortedDays.map((day) => (
          <DayEvents
            key={day}
            day={day}
            events={dayEventPairings[day]}
            onSelectEvent={onSelectEvent}
          />
        ))}
      </ScrollView>
    </View>
  );
};

const DayEvents: React.FC<{ day: string; events: IEvent[]; onSelectEvent: (event: IEvent) => void }> = ({
  day,
  events,
  onSelectEvent,
}) => {
  const dayLabel = useMemo(
    () =>
      new Date(day).toLocaleString(undefined, {
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      }),
    [day]
  );

  return (
    <View style={styles.dayContainer}>
      <Text style={styles.dayText}>{dayLabel}</Text>
      {events.map((event) => (
        <EventRow key={event.id} event={event} onSelectEvent={onSelectEvent} />)
      )}
    </View>
  );
};

const EventRow: React.FC<{ event: IEvent; onSelectEvent: (event: IEvent) => void }> = ({ event, onSelectEvent }) => {
  const eventType = formatEventType(event);
  const color = getEventStatusColor(eventType);

  const firstPhotoUrl = event.photos?.[0]?.photoUrl;

  return (
    <TouchableOpacity style={styles.eventRow} onPress={() => onSelectEvent(event)}>
      <View style={styles.photoTile}>
        {firstPhotoUrl ? (
          <LazyImage
            source={{ uri: firstPhotoUrl }}
            style={styles.photoImage}
            resizeMode="cover"
            placeholder={<View style={styles.photoPlaceholder} />}
            errorPlaceholder={<View style={styles.photoPlaceholder} />}
          />
        ) : (
          <View style={styles.photoPlaceholder} />
        )}
      </View>
      <View style={styles.eventMeta}>
        <View style={styles.eventMetaHeader}>
          <View style={[styles.statusDot, { backgroundColor: color }]} />
          <Text style={styles.eventName} numberOfLines={1}>
            {event.name}
          </Text>
        </View>
        <Text style={styles.rsvpText}>{event.n_rsvps} rsvps</Text>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 8,
    marginTop: 12,
    // Let inner ScrollView manage overflow
  },
  containerFlex: {
    flex: 1,
    minHeight: 0,
  },
  scroll: {},
  scrollFlex: {
    flex: 1,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    paddingHorizontal: 16,
    marginBottom: 4,
  },
  dayContainer: {
    paddingVertical: 8,
  },
  dayText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    paddingHorizontal: 16,
    marginBottom: 6,
  },
  eventRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  photoTile: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  photoImage: {
    width: 40,
    height: 40,
    borderRadius: 8,
  },
  photoPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  eventMeta: {
    flex: 1,
  },
  eventMetaHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  eventName: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '600',
  },
  rsvpText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
});


